import Modal from "react-modal";
import {X} from "react-feather";
import React, {useState} from "react";

const RestoreMergedAccountModal = ({isOpen, onClose, mergedAccount, onRestore}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [showSummary, setShowSummary] = useState(false);
    const [summaryData, setSummaryData] = useState(null);
    const [isDryRun, setIsDryRun] = useState(false);
    const [error, setError] = useState(null);

    const handleClose = () => {
        setShowSummary(false);
        setSummaryData(null);
        setIsDryRun(false);
        setError(null);
        onClose();
    };

    const handleDryRun = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const result = await onRestore(mergedAccount.id, true);

            if (result && result.merged_user_id) {
                setSummaryData(result);
                setIsDryRun(true);
                setShowSummary(true);
            } else {
                throw new Error('Dry run failed');
            }
        } catch (error) {
            console.error('Error during dry run:', error);
            setError('Error during dry run. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRestore = async () => {
        const fromEmail = mergedAccount.from_user.email;
        const toEmail = mergedAccount.to_user.email;
        const fromName = `${mergedAccount.from_user.first_name} ${mergedAccount.from_user.last_name}`;
        const toName = `${mergedAccount.to_user.first_name} ${mergedAccount.to_user.last_name}`;

        const confirmed = window.confirm(
            `⚠️ RESTORE CONFIRMATION ⚠️\n\n` +
            `This will restore the merged account:\n\n` +
            `FROM Account:\n  Email: ${fromEmail}\n  Name: ${fromName}\n\n` +
            `TO Account:\n  Email: ${toEmail}\n  Name: ${toName}\n\n` +
            `This will move data back from TO user to FROM user.\n\n` +
            `Are you absolutely sure you want to proceed?`
        );

        if (!confirmed) {
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const result = await onRestore(mergedAccount.id, false);

            if (result && result.merged_user_id) {
                setSummaryData(result);
                setIsDryRun(false);
                setShowSummary(true);
            } else {
                throw new Error('Restore failed');
            }
        } catch (error) {
            console.error('Error restoring account:', error);
            setError('Error restoring account. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackToSelection = () => {
        setShowSummary(false);
        setSummaryData(null);
        setIsDryRun(false);
        setError(null);
    };

    const renderSummary = () => {
        if (!summaryData) return null;

        const legacyTables = summaryData.legacy || {};
        const nextgenTables = summaryData.nextgen || {};

        const legacyRecords = Object.values(legacyTables).reduce((sum, table) => sum + (table.updated || 0) + (table.inserted || 0), 0);
        const nextgenRecords = Object.values(nextgenTables).reduce((sum, table) => sum + (table.updated || 0) + (table.inserted || 0), 0);
        const totalRecords = legacyRecords + nextgenRecords;

        const bgColor = isDryRun ? 'sl-bg-blue-50' : 'sl-bg-green-50';
        const borderColor = isDryRun ? 'sl-border-blue-200' : 'sl-border-green-200';
        const textColor = isDryRun ? 'sl-text-blue-800' : 'sl-text-green-800';

        return (
            <div className="sl-modal-body sl-max-h-185i sl-h-auto sl-px-6 sl-pb-0 sl-pt-24 sl-overflow-y-auto sl-customized-scrollbar sl-font">
                <div className={`${bgColor} sl-border ${borderColor} sl-rounded-lg sl-p-4 sl-mb-4`}>
                    <p className={`sl-text-sm ${textColor}`}>
                        {isDryRun ? (
                            <>
                                <span className="sl-font-semibold">Dry Run Preview:</span> This is a preview of what will be restored. No actual changes have been made.
                            </>
                        ) : (
                            <>
                                <span className="sl-font-semibold">Restore Complete:</span> The account has been successfully restored.
                            </>
                        )}
                    </p>
                </div>

                <div className="sl-mb-4">
                    <h3 className="sl-text-md sl-font-semibold sl-mb-2">Restore Summary</h3>
                    <p className="sl-text-sm sl-text-gray-600">
                        Total records affected: <span className="sl-font-semibold">{totalRecords}</span>
                    </p>
                    <p className="sl-text-sm sl-text-gray-600">
                        Legacy system: <span className="sl-font-semibold">{legacyRecords}</span> records
                    </p>
                    <p className="sl-text-sm sl-text-gray-600">
                        NextGen system: <span className="sl-font-semibold">{nextgenRecords}</span> records
                    </p>
                </div>

                {legacyRecords > 0 && (
                    <div className="sl-mb-6">
                        <h4 className="sl-text-sm sl-font-semibold sl-mb-2 sl-text-gray-700">Legacy System</h4>
                        <div className="sl-overflow-x-auto">
                            <table className="sl-table sl-table-bordered sl-w-full">
                                <thead>
                                    <tr className="sl-bg-gray-50">
                                        <th className="sl-px-4 sl-py-2 sl-text-left sl-text-xs sl-font-semibold sl-text-gray-700">Table</th>
                                        <th className="sl-px-4 sl-py-2 sl-text-left sl-text-xs sl-font-semibold sl-text-gray-700">Updated</th>
                                        <th className="sl-px-4 sl-py-2 sl-text-left sl-text-xs sl-font-semibold sl-text-gray-700">Inserted</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(legacyTables).map(([tableName, counts]) => (
                                        <tr key={tableName} className="sl-border-b">
                                            <td className="sl-px-4 sl-py-2 sl-text-sm">{tableName}</td>
                                            <td className="sl-px-4 sl-py-2 sl-text-sm">{counts.updated || 0}</td>
                                            <td className="sl-px-4 sl-py-2 sl-text-sm">{counts.inserted || 0}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {nextgenRecords > 0 && (
                    <div className="sl-mb-6">
                        <h4 className="sl-text-sm sl-font-semibold sl-mb-2 sl-text-gray-700">NextGen System</h4>
                        <div className="sl-overflow-x-auto">
                            <table className="sl-table sl-table-bordered sl-w-full">
                                <thead>
                                    <tr className="sl-bg-gray-50">
                                        <th className="sl-px-4 sl-py-2 sl-text-left sl-text-xs sl-font-semibold sl-text-gray-700">Table</th>
                                        <th className="sl-px-4 sl-py-2 sl-text-left sl-text-xs sl-font-semibold sl-text-gray-700">Updated</th>
                                        <th className="sl-px-4 sl-py-2 sl-text-left sl-text-xs sl-font-semibold sl-text-gray-700">Inserted</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(nextgenTables).map(([tableName, counts]) => (
                                        <tr key={tableName} className="sl-border-b">
                                            <td className="sl-px-4 sl-py-2 sl-text-sm">{tableName}</td>
                                            <td className="sl-px-4 sl-py-2 sl-text-sm">{counts.updated || 0}</td>
                                            <td className="sl-px-4 sl-py-2 sl-text-sm">{counts.inserted || 0}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {totalRecords === 0 && (
                    <div className="sl-bg-yellow-50 sl-border sl-border-yellow-200 sl-rounded-lg sl-p-4">
                        <p className="sl-text-sm sl-text-yellow-800">
                            No records found to restore.
                        </p>
                    </div>
                )}
            </div>
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            className="sl-modal-content sl-max-modal"
            overlayClassName="sl-modal sl-block sl-rounded-md sl-bg-gray-900-opacity-50">

            <div className="sl-modal-body">
                <div className="sl-modal-header sl-rounded-md sl-flex sl-justify-between sl-bg-gray-50 sl-text-gray-900 sl-px-6 sl-py-16 sl-border-bottom">
                    <div className="title sl-font-semibold sl-text-lg sl-mr-auto sl-ml-0 sl-font">
                        Restore Merged Account
                    </div>
                    <div className="sl-cursor-pointer sl-btn-link-gray sl-mr-0">
                        <X onClick={handleClose}/>
                    </div>
                </div>
            </div>

            <div className="sl-modal-body">
                {showSummary ? (
                    renderSummary()
                ) : (
                    <div className="sl-modal-body sl-max-h-185i sl-h-auto sl-px-6 sl-pb-0 sl-pt-24 sl-overflow-y-auto sl-customized-scrollbar sl-font">

                        {/* Account Information */}
                        <div className="sl-mb-6 sl-bg-gray-50 sl-border sl-border-gray-200 sl-rounded-lg sl-p-4">
                            <h3 className="sl-text-md sl-font-semibold sl-mb-3">Merged Account Details</h3>

                            <div className="sl-mb-2">
                                <span className="sl-text-sm sl-text-gray-600 sl-font-semibold">Merged Account ID:</span>
                                <span className="sl-text-sm sl-text-gray-900 sl-ml-2">{mergedAccount.id}</span>
                            </div>

                            <div className="sl-mb-2">
                                <span className="sl-text-sm sl-text-gray-600 sl-font-semibold">From User:</span>
                                <span className="sl-text-sm sl-text-gray-900 sl-ml-2">
                                    {mergedAccount.from_user.first_name} {mergedAccount.from_user.last_name} ({mergedAccount.from_user.email})
                                </span>
                            </div>

                            <div className="sl-mb-2">
                                <span className="sl-text-sm sl-text-gray-600 sl-font-semibold">To User:</span>
                                <span className="sl-text-sm sl-text-gray-900 sl-ml-2">
                                    {mergedAccount.to_user.first_name} {mergedAccount.to_user.last_name} ({mergedAccount.to_user.email})
                                </span>
                            </div>

                            {mergedAccount.user && (
                                <div className="sl-mb-2">
                                    <span className="sl-text-sm sl-text-gray-600 sl-font-semibold">Merged By:</span>
                                    <span className="sl-text-sm sl-text-gray-900 sl-ml-2">
                                        {mergedAccount.user.first_name} {mergedAccount.user.last_name}
                                    </span>
                                </div>
                            )}

                            <div className="sl-mb-2">
                                <span className="sl-text-sm sl-text-gray-600 sl-font-semibold">Merged On:</span>
                                <span className="sl-text-sm sl-text-gray-900 sl-ml-2">
                                    {new Date(mergedAccount.created_at).toLocaleString()}
                                </span>
                            </div>
                        </div>

                        {/* Warning Message */}
                        <div className="sl-bg-yellow-50 sl-border sl-border-yellow-200 sl-rounded-lg sl-p-4 sl-mb-4">
                            <p className="sl-text-sm sl-text-yellow-800">
                                <span className="sl-font-semibold">Warning:</span> Restoring this account will move all data back from the TO user to the FROM user. This action should be carefully reviewed.
                            </p>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="sl-bg-red-50 sl-border sl-border-red-200 sl-rounded-lg sl-p-4 sl-mb-4">
                                <p className="sl-text-sm sl-text-red-800">{error}</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="sl-modal-footer sl-px-6 sl-py-16 sl-flex sl-justify-end sl-gap-3 sl-border-top">
                {showSummary ? (
                    <>
                        {isDryRun && (
                            <button
                                className="sl-btn sl-btn-secondary sl-btn-md"
                                onClick={handleBackToSelection}
                                disabled={isLoading}
                            >
                                Back to Selection
                            </button>
                        )}
                        <button
                            className="sl-btn sl-btn-primary sl-btn-md"
                            onClick={handleClose}
                            disabled={isLoading}
                        >
                            {isDryRun ? 'Close' : 'Done'}
                        </button>
                    </>
                ) : (
                    <>
                        <button
                            className="sl-btn sl-btn-secondary sl-btn-md"
                            onClick={handleClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </button>
                        <button
                            className="sl-btn sl-btn-secondary sl-btn-md"
                            onClick={handleDryRun}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Processing...' : 'Try Dry Run'}
                        </button>
                        <button
                            className="sl-btn sl-btn-primary sl-btn-md"
                            onClick={handleRestore}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Processing...' : 'Restore'}
                        </button>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default RestoreMergedAccountModal;
